// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Every number the five seconds are made of, in one file, so `tests/balance.test.ts` can point at
// the constant it just moved and `tests/mechanism.test.ts` can re-derive the rules WITHOUT importing
// the resolver that implements them.
//
// ── THE ONE IDEA THESE NUMBERS ENCODE ───────────────────────────────────────────────────────────
// Nobody has hit points and nothing rolls a die. A shot kills, and a unit only ever shoots what is
// inside the wedge it is watching. So a fight is never "who does more damage" — it is "who was
// already pointed at the door", and every constant below exists to make that a decision you took
// during planning rather than something that happened to you:
//
//   ACQUIRE   how long a clean line must be HELD before the shot goes off. Long enough that a lane
//             can be crossed, short enough that standing in one is fatal.
//   WEDGE     the slice you can shoot inside. Hard-edged, not a modifier: outside it you do not
//             shoot at all, however long you look. This is where the mind-game lives, because a
//             watch is a guess you wrote down and cannot take back for five seconds.
//   STANCE    stalk keeps your draw and costs you ground; move halves it; rush deletes it entirely.
//   RANGE     three units, three ranges, and every one of them can be answered by exactly the thing
//             it cannot reach.
//
// ── WHY THERE IS NO Math.hypot, atan2, sin OR cos IN THE RESOLVER ───────────────────────────────
// Two devices resolve the same five seconds independently and must agree to the bit. IEEE-754
// specifies +, -, *, / and sqrt EXACTLY — every conforming engine returns the identical result — but
// it says nothing about the transcendentals. Math.atan2, Math.sin, Math.cos, Math.pow and even
// Math.hypot are implementation-defined and genuinely do differ in the last place between engines.
// One of them in the tick loop is not a rounding wobble: it is two players watching different films
// of the same turn, on the same orders, with no way to tell which is right.
//
// So the resolver uses only the exact operations, squared distances instead of lengths, and a
// sixteen-entry table of hand-written integer direction vectors instead of an angle.
// `tests/source-hygiene.test.ts` greps for the forbidden ones and `tests/rules.test.ts` proves the
// table is a real circle.

/** Simulation rate. 100 ticks per five-second window; a tick is the smallest thing anyone wins by. */
export const TICK_HZ = 20;
export const WINDOW_TICKS = 100;
/** The window both sides plan. Five seconds is the whole conceit. */
export const WINDOW_S = WINDOW_TICKS / TICK_HZ;

/** Aim is counted in points. A hundred points a tick is the undiminished rate. */
export const AIM_FULL = 100;
/** Walking and shooting: you can do both, and you are less than half as quick on the draw. */
export const AIM_MOVING = 45;

/** Waypoints a player may set per unit per turn. Three is a route; four is a spreadsheet. */
export const MAX_WAYPOINTS = 3;

/** Sixteen watch directions. Quantised in the UI too, so the wire value IS the canonical one. */
export const SECTORS = 16;

export type StanceSpec = { speed: number; aim: number; name: string; short: string };

/**
 * Stance multipliers, in percent, applied to speed and to the aim rate while MOVING. A unit that is
 * standing still always draws at the full rate whatever its stance says — standing still is the
 * strongest thing in the game and it costs you the whole board.
 */
export const STANCES: StanceSpec[] = [
  { name: 'Stalk', short: 'S', speed: 50, aim: 100 },
  { name: 'Move', short: 'M', speed: 100, aim: AIM_MOVING },
  // Rush cannot fire at all. That is what makes throwing a unit away to freeze a mast a real play
  // rather than a mistake: it is fast, it is loud, and it is unarmed.
  { name: 'Rush', short: 'R', speed: 160, aim: 0 },
];

export interface UnitSpec {
  id: 'scout' | 'line' | 'long';
  name: string;
  /** One line the player reads on the chip. */
  blurb: string;
  /** Ticks of held line, at the full rate, before the shot goes off. */
  acquire: number;
  /** Tiles: how far it can see AND shoot. Seeing is all round; shooting is the wedge. */
  range: number;
  /** Half-width of the firing wedge, in sixteenths of a turn. */
  wedge: number;
  /** Tiles per second at Move. */
  speed: number;
  /** The glyph drawn on the chip — colour is never the only channel. */
  glyph: string;
}

/**
 * Three units, one of each, no drafting: the asymmetry is positional, never a loadout screen.
 *
 * ── WHY THE LONGEST RANGE IS 10 AND NOT 13 ──────────────────────────────────────────────────────
 * The spawn rows are thirteen apart. At range 13 a Marksman that never moved was inside the enemy
 * spawn column on the FIRST TICK OF THE FIRST TURN, and the sim measured a kill in the opening
 * window of 70% of matches — before either player had made a decision the other could respond to.
 * Sealing the spawn-to-spawn sight lines does not fix it, because the shot does not need the spawn
 * TILE, only somewhere near it. Ten tiles is shorter than the board is long, which makes the opening
 * a movement problem instead of a lottery over which Marksman happened to inherit a lane.
 *
 * The numbers are a triangle rather than a ladder. The scout draws in a third of a second and dies
 * to anything it cannot reach; the marksman owns a lane nothing else can answer inside and is a
 * statue the moment it has to move; the line is the one that can do both badly. Every one of them
 * loses to exactly the situation it cannot get out of, and which situation the board offers is what
 * the map generator is really choosing.
 */
export const UNITS: UnitSpec[] = [
  {
    id: 'scout',
    name: 'Scout',
    blurb: 'Fast, wide, short. Draws first at knife range and cannot answer a lane.',
    acquire: 10,
    range: 5,
    wedge: 3,
    speed: 3.6,
    glyph: '▲',
  },
  {
    id: 'line',
    name: 'Line',
    blurb: 'The middle of everything. Wins the fights nobody planned.',
    acquire: 17,
    range: 8,
    wedge: 2,
    speed: 2.8,
    glyph: '■',
  },
  {
    id: 'long',
    name: 'Marksman',
    blurb: 'Owns a lane from outside everyone else’s reach. Useless the moment it moves.',
    acquire: 26,
    range: 10,
    wedge: 1,
    speed: 1.9,
    glyph: '◆',
  },
];

/** How near a unit must be to a mast tile to be counted as holding it. */
export const MAST_FIELD = 1;

/**
 * How long the room waits for a plan before playing the seat as "hold everything".
 *
 * A turn that waits for ever on somebody who put their phone down is the deadlock this fleet keeps
 * shipping. Holding is the honest default: it is a legal order, it is what an unattended unit would
 * do, and it is never silently good for the absent player.
 */
export const PLAN_MS = 75_000;
/** …and the point past which a peer that has said nothing at all is treated as gone. */
export const SILENCE_MS = 15_000;
/** Heartbeat, so silence is detectable without relying on onPeerLeave (which misses a closed tab). */
export const BEAT_MS = 2_000;
